<template>
  <div class="editor-view-container overflow-hidden">
    <FlsaFormSkeletonLoader v-if="pageLoading" />

    <EditorLayout
      class="h-100 w-100"
      :side-by-side-view="false"
    >
      <template
        v-if="!pageLoading"
        #content
      >
        <FlsaReviewContent
          class="w-100"
          :section="activeFlsaSection"
          :readonly="false"
          :submitted-at="null"
          :initial-data="null"
          :is-submitting="isSubmitting"
          @update:section="activeFlsaSection = $event"
          @submit="handleFlsaSubmit"
        />
      </template>

      <template
        #left
      >
        <FlsaReviewStepsSidebar
          :section="activeFlsaSection"
          :jurisdiction="flsaFormJurisdiction"
          @update:section="activeFlsaSection = $event"
        />
      </template>

      <template #right>
        <FlsaSubmissionHistory
          v-if="flsaFormUlid && (
            flsaSubmissionProfileUlids.jobProfileUlid
            || flsaSubmissionProfileUlids.draftJobProfileUlid
          )"
          :flsa-form-ulid="flsaFormUlid"
          :job-profile-ulid="flsaSubmissionProfileUlids.jobProfileUlid"
          :draft-job-profile-ulid="flsaSubmissionProfileUlids.draftJobProfileUlid"
        />
      </template>
    </EditorLayout>
  </div>
</template>

<script setup>
import {
  computed,
  onMounted,
  ref,
} from 'vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useAppLoading } from '@/stores/app/loading';
import { useAppRoute } from '@/stores/app/route';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useCurrentJobProfile } from '@/stores/jobProfiles/currentJobProfile';
import { getFlsaForms, createFlsaSubmission } from '@/api/jobProfile';
import { findEffectiveFlsaForm, getFlsaSubmissionProfileUlids } from '@/helpers/flsa';
import { useFlsaSection } from '@/composables/useFlsaSection';
import FlsaFormSkeletonLoader from '@/components/shared/SkeletonLoaders/editor/FlsaFormSkeletonLoader.vue';
import EditorLayout from '@/components/editor/EditorLayout.vue';
import FlsaReviewContent from '@/components/jobProfiles/editor/FlsaReviewContent.vue';
import FlsaReviewStepsSidebar from '@/components/jobProfiles/editor/FlsaReviewStepsSidebar.vue';
import FlsaSubmissionHistory from '@/components/jobProfiles/editor/FlsaSubmissionHistory.vue';
import { useTracking } from '@/stores/tracking/tracking';

const router = useRouter();

const { pageLoading } = storeToRefs(useAppLoading());
const { setLoading } = useAppLoading();

const { route } = storeToRefs(useAppRoute());

const { resetJobProfile, loadJobProfile } = useCurrentJobProfile();
const {
  jobProfile,
  isHrisProfile,
} = storeToRefs(useCurrentJobProfile());

const { activeFlsaSection } = useFlsaSection({ defaultSection: 'eligibility' });

const flsaFormUlid = ref(null);
const flsaFormJurisdiction = ref('federal');
const isSubmitting = ref(false);

const flsaSubmissionProfileUlids = computed(() => getFlsaSubmissionProfileUlids({
  jobProfile: jobProfile.value,
  isHrisProfile: isHrisProfile.value,
}));

// Normalizes `?hrisUlid=` vs `?draftUlid=` for initial profile load (HRIS ULID wins if both exist).
const resolveProfileContextFromCreateQuery = (query = route.value?.query) => {
  const hrisUlid = query?.hrisUlid;
  const draftUlid = query?.draftUlid;

  if (hrisUlid) {
    return { ulid: hrisUlid, isHris: true };
  }

  if (draftUlid) {
    return { ulid: draftUlid, isHris: false };
  }

  return null;
};

const handleFlsaSubmit = async (payload) => {
  const profileUlids = flsaSubmissionProfileUlids.value;

  if (!profileUlids.jobProfileUlid && !profileUlids.draftJobProfileUlid) {
    useAppSnackbar().addErrorSnackbar({ text: 'Missing profile context for FLSA submit.' });

    return;
  }

  if (!flsaFormUlid.value) {
    useAppSnackbar().addErrorSnackbar({ text: 'FLSA form is not ready to submit. Try refreshing the page.' });

    return;
  }

  isSubmitting.value = true;

  try {
    const result = await createFlsaSubmission({
      jobProfileUlid: profileUlids.jobProfileUlid || undefined,
      jobProfileVersionUlid: profileUlids.jobProfileVersionUlid || undefined,
      draftJobProfileUlid: profileUlids.draftJobProfileUlid || undefined,
      draftRevisionUlid: profileUlids.draftJobProfileUlid ? jobProfile.value?.revision_ulid : undefined,
      flsaFormUlid: flsaFormUlid.value,
      dataJson: payload,
    });

    const submissionUlid = result.ulid ?? result.submission_ulid;

    if (!submissionUlid) {
      useAppSnackbar().addErrorSnackbar({ text: 'Submit succeeded but no submission id was returned.' });

      return;
    }

    useAppSnackbar().addSuccessSnackbar({ text: 'FLSA form submitted successfully.' });

    useTracking().flsaFormSubmitted({ jurisdiction: flsaFormJurisdiction.value });

    if (profileUlids.jobProfileUlid) {
      await router.push({
        name: 'job-profile-hris-view',
        params: { id: profileUlids.jobProfileUlid },
        query: { sidebar: 'flsa' },
      });
    } else if (profileUlids.draftJobProfileUlid) {
      await router.push({
        name: 'job-profile-view',
        params: { id: profileUlids.draftJobProfileUlid },
        query: { sidebar: 'flsa' },
      });
    }
  } catch (error) {
    useAppSnackbar().addErrorSnackbar({ text: `Failed to submit FLSA form — ${error.message}` });

    throw error;
  } finally {
    isSubmitting.value = false;
  }
};

const load = async (query = route.value?.query) => {
  const resolvedProfile = resolveProfileContextFromCreateQuery(query);

  if (!resolvedProfile) {
    resetJobProfile();

    flsaFormUlid.value = null;
    flsaFormJurisdiction.value = 'federal';

    useAppSnackbar().addErrorSnackbar({
      text: 'Add draftUlid or hrisUlid to create an FLSA form.',
    });

    return;
  }

  try {
    setLoading({ pageLoading: true });

    await loadJobProfile({
      ulid: resolvedProfile.ulid,
      isHris: resolvedProfile.isHris,
      rescore: true,
    });

    flsaFormUlid.value = null;
    flsaFormJurisdiction.value = 'federal';

    // loadJobProfile clears pageLoading when it finishes — keep skeleton through forms fetch.
    setLoading({ pageLoading: true });

    try {
      const forms = await getFlsaForms();
      const formsList = Array.isArray(forms) ? forms : [];
      const queryFormUlid = query?.flsaFormUlid;

      let selectedForm = null;

      if (queryFormUlid) {
        selectedForm = findEffectiveFlsaForm(formsList, { ulid: queryFormUlid });

        if (!selectedForm) {
          useAppSnackbar().addErrorSnackbar({
            text: 'Selected FLSA form is not available.',
          });
        }
      } else {
        selectedForm = findEffectiveFlsaForm(formsList, { jurisdiction: 'federal' });

        if (!selectedForm) {
          useAppSnackbar().addErrorSnackbar({
            text: 'No federal FLSA form is available.',
          });
        }
      }

      flsaFormUlid.value = selectedForm?.ulid;
      flsaFormJurisdiction.value = selectedForm?.jurisdiction || 'federal';
    } catch (error) {
      useAppSnackbar().addErrorSnackbar({ text: `Failed to load FLSA forms — ${error.message}` });
      throw error;
    }
  } finally {
    setLoading({ pageLoading: false });
  }
};

onMounted(async () => {
  useTracking().trackPageLoad({ page: 'FLSA Form Create: Open' });

  await load();
});
</script>

<style scoped lang="less">
.editor-view-container {
  :deep(.editor-layout > .content) {
    height: calc(100vh - 60px);
  }

  :deep(.content-footer) {
    display: none;
  }
}
</style>
